/**
 * AOP 2027 Planning — the Overview tab's company figures (David, Oct 26 2026).
 *
 * The same tiles, table and cost chart as a department tab, over the company totals the proxy adds
 * up (`companyTotals` in `proxy-server/lib/aopTargets.js`), then one bar per department for its
 * plan year against its budget. Rendered only when the proxy sent `company` — that is, for a viewer
 * who sees every department — so nothing here sums a department the viewer could not open.
 *
 * Plan and budget cover the same departments; the ones left out for want of a budget are named
 * under the table, as are the reasons a cost is blank or understated.
 */
import type { AopCompanyTargets as CompanyTargets, AopDepartmentTargets } from '../../services/aopPlanningApi';
import type { MonthGroup } from '../internalPlayground/OrgChartTable';
import { AopTargetsFigures } from './AopTargetsTable';
import { AopCostChart, AopDepartmentGapChart } from './AopCostCharts';

/** "Fleet and Tech" / "Fleet, Sales and Tech". */
const listed = (labels: string[]) =>
  labels.length < 2 ? labels.join('') : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;

/** The company block on the Overview tab — see the module note. */
export function AopCompanyTargets({
  company,
  targets,
  columns,
  groups,
  expandedGroupKeys,
  onSelectDepartment,
}: {
  company: CompanyTargets;
  targets: Record<string, AopDepartmentTargets>;
  columns: string[];
  groups: MonthGroup[];
  expandedGroupKeys: Set<string>;
  onSelectDepartment?: (label: string) => void;
}) {
  const { fte, cost } = company.leftOut;
  const sameLeftOut = fte.length === cost.length && fte.every((label) => cost.includes(label));
  return (
    <div className="space-y-3">
      <AopTargetsFigures
        label="FINN"
        months={company.months}
        columns={columns}
        groups={groups}
        expandedGroupKeys={expandedGroupKeys}
        notes={
          <>
            {sameLeftOut && fte.length > 0 && <p>Not counted yet, for want of a budget: {listed(fte)}.</p>}
            {!sameLeftOut && fte.length > 0 && <p>Not in the FTE figures, for want of a budget FTE: {listed(fte)}.</p>}
            {!sameLeftOut && cost.length > 0 && <p>Not in the cost figures, for want of a budget or baseline cost: {listed(cost)}.</p>}
            {company.bandsUnavailable && (
              <p>The salary bands could not be read, so the drafts are not costed and plan cost is blank. Refresh in a moment.</p>
            )}
            {company.costHidden && !company.bandsUnavailable && (
              <p>Draft and plan cost are hidden: a draft in some department uses a salary band you can&apos;t see.</p>
            )}
            {company.uncostedDrafts > 0 && (
              <p>
                {company.uncostedDrafts} {company.uncostedDrafts === 1 ? 'draft has' : 'drafts have'} no cost yet and
                {company.uncostedDrafts === 1 ? ' counts' : ' count'} as 0 € — each needs a track and level, a start date and
                a salary band.
              </p>
            )}
            <p>
              The company is the departments added up: plan and budget over the same departments. Plan = committed FTE
              and cost plus the AOP drafts.
            </p>
          </>
        }
      />
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <AopCostChart label="FINN" months={company.months} groups={groups} />
        <AopDepartmentGapChart byDepartment={targets} groups={groups} onSelect={onSelectDepartment} />
      </div>
    </div>
  );
}
